import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';
import { OrderDetail } from './orders/OrderDetail';
import {
  DEFAULT_STOCK_LIST_FILTERS,
  StockFiltersModal,
  countActiveStockFilters,
} from './orders/StockFiltersModal';
import { canonicalStatus, statusLabel, statusMeta, TONE_BG } from './orders/workflowConstants';
import { formatDisplayDate } from './orders/orderFormHelpers';
import { api, extractErrorMessage } from '../api/client';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { useCommercialAccess } from '../permissions/useCommercialAccess';
import { parseStockRoute, stockDetailPath, STOCK_BASE_PATH } from './stock/stockRouteUtils';

function formatStockedAt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function formatShort(s, max) {
  if (s == null || String(s).trim() === '') return '—';
  const t = String(s).trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function stockedTimeMs(o) {
  if (!o.stockedAt) return 0;
  const t = new Date(o.stockedAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Page size used to collect all in-stock orders (largest the server allows). */
const STOCK_PAGE_SIZE = 200;

/**
 * Warehouse stock: goods completed and stored before dispatch.
 * Emphasises physical quantity, who to deliver to, and when stock was received (FIFO).
 */
function StockOrders() {
  const canSeeCommercial = useCommercialAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const stockRoute = useMemo(
    () => parseStockRoute(location.pathname),
    [location.pathname]
  );

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [stockFilters, setStockFilters] = useState(() => ({ ...DEFAULT_STOCK_LIST_FILTERS }));
  const [filtersOpen, setFiltersOpen] = useState(false);

  const goToStockList = useCallback(() => {
    navigate({ pathname: STOCK_BASE_PATH, search: location.search });
  }, [navigate, location.search]);

  const openStockOrder = useCallback(
    (orderId) => {
      navigate(stockDetailPath(orderId));
    },
    [navigate]
  );

  const loadStock = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      // Stock tabs, counts, search and sorting work on all in-stock orders, so collect every
      // page of GET /api/orders (in-stock only) before showing them.
      const all = [];
      let after = null;
      do {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get('/api/orders', {
          params: { status: 'IN_STOCK', size: STOCK_PAGE_SIZE, ...(after ? { after } : {}) },
        });
        all.push(...(Array.isArray(data?.items) ? data.items : []));
        after = data?.nextCursor || null;
      } while (after);
      setOrders(all);
    } catch (e) {
      setPageError(extractErrorMessage(e, 'Failed to load stock'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (stockRoute.screen !== 'list') return;
    loadStock();
  }, [stockRoute.screen, loadStock]);

  const { stockTab, query, sort } = stockFilters;
  const q = query.trim().toLowerCase();
  const stockFilterBadge = countActiveStockFilters(stockFilters);

  const stockTabCounts = useMemo(() => {
    const all = orders.length;
    const ready = orders.filter((o) => !o.onHold).length;
    const hold = orders.filter((o) => o.onHold).length;
    return { all, ready, hold };
  }, [orders]);

  const afterTab = useMemo(() => {
    if (stockTab === 'ready') return orders.filter((o) => !o.onHold);
    if (stockTab === 'hold') return orders.filter((o) => o.onHold);
    return orders;
  }, [orders, stockTab]);

  const filtered = useMemo(() => {
    if (!q) return afterTab;
    return afterTab.filter((o) => {
      const blob = [
        o.orderCode,
        ...(canSeeCommercial ? [o.partyName, o.partyMobile, o.partyAddress, o.deliveryLabel] : []),
        o.designSampleNo,
        o.designConceptName,
        o.designCategoryName,
        o.designSubCategoryName,
        o.remark,
        o.beamColor,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [afterTab, q, canSeeCommercial]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const byCode = (a, b) => (a.orderCode || '').localeCompare(b.orderCode || '', undefined, { numeric: true });
    switch (sort) {
      case 'stocked_asc':
        return arr.sort((a, b) => stockedTimeMs(a) - stockedTimeMs(b));
      case 'stocked_desc':
        return arr.sort((a, b) => stockedTimeMs(b) - stockedTimeMs(a));
      case 'party_asc':
        return arr.sort((a, b) => (a.partyName || '').localeCompare(b.partyName || '', undefined, { sensitivity: 'base' }));
      case 'pieces_desc':
        return arr.sort((a, b) => (Number(b.totalPieces) || 0) - (Number(a.totalPieces) || 0));
      case 'order_code_asc':
        return arr.sort(byCode);
      default:
        return arr;
    }
  }, [filtered, sort]);

  const summary = useMemo(() => {
    const orderCount = filtered.length;
    const totalPieces = filtered.reduce((s, o) => s + (Number(o.totalPieces) || 0), 0);
    const onHold = filtered.filter((o) => o.onHold).length;
    return { orderCount, totalPieces, onHold };
  }, [filtered]);

  if (stockRoute.screen === 'detail') {
    return (
      <div className="mx-auto flex min-h-0 flex-1 w-full max-w-[1500px] flex-col">
        <OrderDetail
          key={stockRoute.orderId}
          orderId={stockRoute.orderId}
          onBack={goToStockList}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-3">
      <Card>
        <CardBody className="space-y-3 !p-3 sm:!p-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setFiltersOpen(true)}
              className="w-full sm:w-auto"
            >
              <svg
                className="mr-1.5 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filters
              {stockFilterBadge > 0 && (
                <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                  {stockFilterBadge}
                </span>
              )}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={loadStock} disabled={loading} className="w-full sm:w-auto">
              Refresh
            </Button>
          </div>

          <AlertMessage type="error" message={pageError} onClose={() => setPageError('')} />

          {!loading && orders.length > 0 && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 sm:px-4 sm:py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">Orders in list</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900 sm:text-2xl">{summary.orderCount}</p>
                <p className="mt-0.5 text-xs text-slate-500">After filters are applied</p>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 px-3 py-2.5 sm:px-4 sm:py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 sm:text-[11px]">Total pieces (physical)</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-sky-900 sm:text-2xl">{summary.totalPieces.toLocaleString()}</p>
                <p className="mt-0.5 text-xs text-sky-800/80">Good units waiting in stock</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2.5 sm:px-4 sm:py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 sm:text-[11px]">On hold in stock</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-amber-900 sm:text-2xl">{summary.onHold}</p>
                <p className="mt-0.5 text-xs text-amber-800/90">Do not ship until hold is released</p>
              </div>
            </div>
          )}

          {loading ? (
            <p className="py-12 text-center text-slate-500">Loading stock…</p>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full min-w-[880px] text-left text-sm">
                  <thead className="bg-slate-100/90 text-xs font-bold uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2.5 w-14" scope="col" aria-label="Design" />
                      <th className="px-3 py-2.5 whitespace-nowrap" scope="col">Put in stock</th>
                      <th className="px-3 py-2.5" scope="col">Order</th>
                      {canSeeCommercial ? (
                        <th className="px-3 py-2.5 min-w-[8rem]" scope="col">Party & contact</th>
                      ) : null}
                      <th className="px-3 py-2.5 min-w-[7rem]" scope="col">Design</th>
                      <th className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap" scope="col">Pcs</th>
                      <th className="px-3 py-2.5 text-right tabular-nums" scope="col">Pick</th>
                      {canSeeCommercial ? (
                        <th className="px-3 py-2.5 min-w-[6rem]" scope="col">Delivery</th>
                      ) : null}
                      <th className="px-3 py-2.5 min-w-[5rem]" scope="col">Note</th>
                      <th className="px-3 py-2.5 w-28" scope="col" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {sorted.map((o) => (
                      <StockTableRow key={o.id} order={o} onOpen={() => openStockOrder(o.id)} canSeeCommercial={canSeeCommercial} />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-4">
                {sorted.map((o) => (
                  <StockMobileCard key={o.id} order={o} onOpen={() => openStockOrder(o.id)} canSeeCommercial={canSeeCommercial} />
                ))}
              </div>
            </>
          )}

          {!loading && orders.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-12 text-center">
              <p className="text-slate-600">No orders in stock.</p>
              <p className="mt-2 text-sm text-slate-500">
                When an order is <span className="font-semibold">Ready for dispatch</span>, use{' '}
                <span className="font-semibold">Put in stock</span> on the order to add it here.
              </p>
            </div>
          )}

          {!loading && orders.length > 0 && afterTab.length === 0 && (
            <p className="py-6 text-center text-sm text-amber-800">
              {stockTab === 'hold' ? 'No orders on hold in stock right now.' : 'No orders in this view.'}
            </p>
          )}

          {!loading && orders.length > 0 && afterTab.length > 0 && sorted.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">No matches for “{query.trim()}”.</p>
          )}
        </CardBody>
      </Card>
      <StockFiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={stockFilters}
        onApply={setStockFilters}
        tabCounts={stockTabCounts}
      />
    </div>
  );
}

function StockTableRow({ order, onOpen, canSeeCommercial = true }) {
  const canonical = canonicalStatus(order.workflowStatus);
  const tone = statusMeta(order.workflowStatus).tone;
  const pcs = Number(order.totalPieces) || 0;
  const designTitle = [order.designCategoryName, order.designSubCategoryName].filter(Boolean).join(' · ') || '—';

  return (
    <tr className="hover:bg-slate-50/80">
      <td className="px-3 py-2 align-middle">
        <div className="h-10 w-10 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {order.designImage ? (
            <AuthenticatedImage src={order.designImage} alt="" className="h-full w-full object-cover" loading="lazy" thumbnail />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">—</div>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top whitespace-nowrap text-slate-800">
        <span className="font-medium">{formatStockedAt(order.stockedAt)}</span>
      </td>
      <td className="px-3 py-2 align-top">
        <p className="font-bold text-slate-900">{order.orderCode || '—'}</p>
        <p className="text-xs text-slate-500">Order date {formatDisplayDate(order.orderDate) || '—'}</p>
        {order.beamColor ? <p className="text-xs text-slate-600">Beam: {order.beamColor}</p> : null}
      </td>
      {canSeeCommercial ? (
        <td className="px-3 py-2 align-top">
          <p className="font-semibold text-slate-900">{order.partyName || '—'}</p>
          {order.partyMobile ? (
            <a
              href={`tel:${String(order.partyMobile).replace(/\s/g, '')}`}
              className="text-sm text-primary-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {order.partyMobile}
            </a>
          ) : (
            <p className="text-xs text-slate-500">—</p>
          )}
        </td>
      ) : null}
      <td className="px-3 py-2 align-top text-slate-800">
        <p className="font-medium">{order.designSampleNo || '—'}</p>
        {order.designConceptName ? <p className="text-xs text-slate-600 line-clamp-2">{order.designConceptName}</p> : null}
        <p className="text-[11px] text-slate-500 mt-0.5" title={designTitle}>{formatShort(designTitle, 42)}</p>
      </td>
      <td className="px-3 py-2 align-top text-right font-bold tabular-nums text-slate-900">{pcs.toLocaleString()}</td>
      <td className="px-3 py-2 align-top text-right tabular-nums text-slate-700">{order.pick ?? '—'}</td>
      {canSeeCommercial ? (
        <td className="px-3 py-2 align-top text-slate-700">
          <p className="line-clamp-2" title={order.deliveryLabel || order.partyAddress || ''}>
            {order.deliveryLabel || formatShort(order.partyAddress, 48) || '—'}
          </p>
        </td>
      ) : null}
      <td className="px-3 py-2 align-top text-slate-600 text-xs" title={order.remark || ''}>
        {order.remark ? formatShort(order.remark, 36) : '—'}
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex flex-col items-stretch gap-1.5">
          <span
            className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${
              TONE_BG[tone] || TONE_BG.slate
            }`}
          >
            {statusLabel(canonical)}
          </span>
          {order.onHold && (
            <span className="w-fit rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">Hold</span>
          )}
          <Button type="button" size="sm" variant="primary" onClick={onOpen}>
            Open
          </Button>
        </div>
      </td>
    </tr>
  );
}

function StockMobileCard({ order, onOpen, canSeeCommercial = true }) {
  const canonical = canonicalStatus(order.workflowStatus);
  const tone = statusMeta(order.workflowStatus).tone;
  const pcs = Number(order.totalPieces) || 0;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex justify-between gap-2 border-b border-slate-100 pb-3">
        <div>
          <p className="text-xs font-semibold text-slate-500">Put in stock</p>
          <p className="text-sm font-bold text-sky-900">{formatStockedAt(order.stockedAt)}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-slate-900">{order.orderCode}</p>
          <p className="text-xs text-slate-500">{formatDisplayDate(order.orderDate)}</p>
        </div>
      </div>
      <div className="mt-3 flex gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
          {order.designImage ? (
            <AuthenticatedImage src={order.designImage} alt="" className="h-full w-full object-cover" loading="lazy" thumbnail />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          {canSeeCommercial ? (
            <>
              <p className="font-bold text-slate-900">{order.partyName}</p>
              {order.partyMobile ? (
                <a href={`tel:${String(order.partyMobile).replace(/\s/g, '')}`} className="text-sm text-primary-600 font-medium">
                  {order.partyMobile}
                </a>
              ) : null}
            </>
          ) : null}
          <p className={`text-sm text-slate-700 ${canSeeCommercial ? 'mt-1' : ''}`}>
            <span className="font-semibold">D.No</span> {order.designSampleNo}
            {order.designConceptName ? ` — ${order.designConceptName}` : ''}
          </p>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:text-sm">
        <div className="rounded-lg bg-slate-50 px-2 py-1.5">
          <dt className="font-semibold text-slate-500">Pieces</dt>
          <dd className="text-lg font-bold tabular-nums text-slate-900">{pcs.toLocaleString()}</dd>
        </div>
        <div className="rounded-lg bg-slate-50 px-2 py-1.5">
          <dt className="font-semibold text-slate-500">Pick</dt>
          <dd className="text-lg font-bold tabular-nums text-slate-900">{order.pick ?? '—'}</dd>
        </div>
        {canSeeCommercial ? (
          <div className="col-span-2 rounded-lg bg-slate-50 px-2 py-1.5">
            <dt className="font-semibold text-slate-500">Deliver / location</dt>
            <dd className="text-slate-800">{order.deliveryLabel || formatShort(order.partyAddress, 120) || '—'}</dd>
          </div>
        ) : null}
        {order.remark ? (
          <div className="col-span-2 rounded-lg border border-amber-100 bg-amber-50/50 px-2 py-1.5">
            <dt className="font-semibold text-amber-900/80">Remark</dt>
            <dd className="text-slate-800">{order.remark}</dd>
          </div>
        ) : null}
        {order.beamColor ? (
          <div className="col-span-2 text-xs text-slate-600">
            <span className="font-semibold">Beam</span> {order.beamColor}
          </div>
        ) : null}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${
            TONE_BG[tone] || TONE_BG.slate
          }`}
        >
          {statusLabel(canonical)}
        </span>
        {order.onHold && <span className="rounded bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900">On hold</span>}
        <Button className="ml-auto" type="button" size="sm" variant="primary" onClick={onOpen}>
          Open — dispatch when ready
        </Button>
      </div>
    </article>
  );
}

export default StockOrders;
